// Seeker-facing session extension flow.
// Uses the production pricing/extension APIs:
//   GET  /api/pricing/extension-price
//   POST /api/pricing/extension/request
//   POST /api/pricing/extension/payment-link
//   POST /api/pricing/extension/verify-payment
//
// Razorpay checkout is opened in-place. Status transitions:
//   idle -> requesting -> awaiting_payment -> paid | failed | cancelled
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Clock, Plus, Minus, CreditCard, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Map session.channel -> backend pricing session_type
const CHANNEL_TO_TYPE = {
  chat: 'chat',
  voice: 'voice_call',
  video: 'tele_session',
};

const MIN_BUNDLES = 1;     // 15 minutes
const MAX_BUNDLES = 4;     // 60 minutes
const BUNDLE_MINUTES = 15;

export default function SessionExtension({
  sessionId,
  session,                 // optional: pass current session for channel inference
  user,                    // current authenticated user (for Razorpay prefill)
  onExtensionPaid,
}) {
  const [bundles, setBundles] = useState(1);
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const [phase, setPhase] = useState('idle'); // idle | requesting | awaiting_payment | paid | failed | cancelled
  const [extensionReq, setExtensionReq] = useState(null);
  const [error, setError] = useState(null);

  const minutes = bundles * BUNDLE_MINUTES;
  const sessionType = useMemo(
    () => CHANNEL_TO_TYPE[session?.channel] || 'chat',
    [session?.channel],
  );

  // Load Razorpay script (idempotent)
  useEffect(() => {
    if (document.getElementById('razorpay-checkout-js')) return;
    const s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Live price fetch whenever bundle count or session type changes
  useEffect(() => {
    let cancelled = false;
    setPriceLoading(true);
    axios
      .get(`${API}/pricing/extension-price`, {
        params: { session_type: sessionType, extension_minutes: minutes },
      })
      .then((res) => {
        if (!cancelled) setPrice(res.data);
      })
      .catch(() => {
        if (!cancelled) setPrice(null);
      })
      .finally(() => {
        if (!cancelled) setPriceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionType, minutes]);

  const adjust = (delta) => {
    setBundles((b) => Math.max(MIN_BUNDLES, Math.min(MAX_BUNDLES, b + delta)));
  };

  const proceedToPayment = async (ext) => {
    try {
      const linkRes = await axios.post(
        `${API}/pricing/extension/payment-link`,
        null,
        { params: { extension_request_id: ext.id, seeker_user_id: user.id } },
      );
      setPhase('awaiting_payment');
      openRazorpay(ext.id, linkRes.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not start payment';
      setError(detail);
      setPhase('failed');
      toast.error(detail);
    }
  };

  const startFlow = async () => {
    if (!sessionId || !user) return;
    setError(null);
    setPhase('requesting');
    try {
      // 1. Create extension request.
      //    care_seeker -> 'pending' (counsellor must explicitly accept first)
      //    care_provider -> auto-approved (initiating IS explicit acceptance)
      const reqRes = await axios.post(
        `${API}/pricing/extension/request`,
        { session_id: sessionId, extension_minutes: minutes },
        { params: { user_id: user.id, user_role: user.role } },
      );
      const ext = reqRes.data.extension_request;
      setExtensionReq(ext);

      if (ext.status === 'approved' || ext.status === 'payment_pending') {
        await proceedToPayment(ext);
      } else {
        // Pending — wait for the counsellor to accept (NO seeker-side bypass)
        setPhase('awaiting_approval');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not start extension';
      setError(detail);
      setPhase('failed');
      toast.error(detail);
    }
  };

  // Poll for counsellor approval, then move to payment
  useEffect(() => {
    if (phase !== 'awaiting_approval' || !extensionReq) return undefined;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/pricing/extension/status/${extensionReq.id}`);
        const st = res.data.status;
        if (st === 'approved' || st === 'payment_pending') {
          clearInterval(iv);
          await proceedToPayment(res.data);
        } else if (['rejected', 'expired', 'cancelled'].includes(st)) {
          clearInterval(iv);
          setError('The counsellor did not approve the extension.');
          setPhase('failed');
        }
      } catch (_) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, extensionReq]);

  const openRazorpay = (extensionId, order) => {
    if (!window.Razorpay) {
      toast.error('Payment SDK not loaded — please retry in a moment');
      setPhase('failed');
      return;
    }
    const options = {
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpay_order_id,
      name: 'Veyonika',
      description: `Extend session by ${minutes} minutes`,
      prefill: order.prefill || {},
      notes: order.notes || {},
      handler: async (response) => {
        try {
          await axios.post(`${API}/pricing/extension/verify-payment`, null, {
            params: {
              extension_request_id: extensionId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
          });
          setPhase('paid');
          toast.success(`Session extended by ${minutes} minutes`);
          onExtensionPaid?.({ minutes, extension_request_id: extensionId });
        } catch (err) {
          setError(err.response?.data?.detail || 'Payment verification failed');
          setPhase('failed');
        }
      },
      modal: {
        ondismiss: () => {
          setPhase('cancelled');
        },
      },
      theme: { color: '#7C3AED' },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // ===== STATE-DRIVEN RENDER =====
  if (phase === 'paid') {
    return (
      <div className="card p-4 bg-emerald-50 border-emerald-200" data-testid="extension-paid">
        <div className="flex items-start gap-3">
          <CheckCircle className="text-emerald-600 shrink-0" size={22} />
          <div>
            <h4 className="font-medium text-emerald-800">Session extended</h4>
            <p className="text-sm text-emerald-700 mt-1">
              +{minutes} minutes • ₹{price?.total_price_inr ?? '—'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="card p-4 bg-red-50 border-red-200" data-testid="extension-failed">
        <div className="flex items-start gap-3">
          <XCircle className="text-red-600 shrink-0" size={22} />
          <div className="flex-1">
            <h4 className="font-medium text-red-800">Could not extend session</h4>
            <p className="text-sm text-red-700 mt-1">{error || 'Please try again.'}</p>
            <button
              onClick={() => { setError(null); setPhase('idle'); }}
              className="mt-3 text-sm text-red-700 underline"
              data-testid="extension-retry-btn"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'cancelled') {
    return (
      <div className="card p-4 bg-gray-50 border-gray-200" data-testid="extension-cancelled">
        <div className="flex items-start gap-3">
          <XCircle className="text-gray-500 shrink-0" size={22} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">Extension cancelled</h4>
            <p className="text-sm text-gray-600 mt-1">No payment was charged.</p>
            <button
              onClick={() => setPhase('idle')}
              className="mt-3 text-sm text-primary underline"
              data-testid="extension-resume-btn"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'awaiting_approval') {
    return (
      <div className="card p-4 bg-amber-50 border-amber-200" data-testid="extension-awaiting-approval">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin text-amber-600 shrink-0" size={22} />
          <div className="flex-1">
            <h4 className="font-medium text-amber-800">Waiting for counsellor to accept</h4>
            <p className="text-sm text-amber-700 mt-1">
              Your request for +{minutes} min was sent. Payment opens automatically once the counsellor accepts.
            </p>
            <button
              onClick={() => setPhase('idle')}
              className="mt-3 text-sm text-amber-700 underline"
              data-testid="extension-cancel-request-btn"
            >
              Cancel request
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'awaiting_payment' || phase === 'requesting') {
    return (
      <div className="card p-4 bg-purple-50 border-purple-200" data-testid="extension-pending">
        <div className="flex items-center gap-3">
          <Loader2 className="animate-spin text-purple-600" size={22} />
          <div>
            <h4 className="font-medium text-purple-800">
              {phase === 'requesting' ? 'Preparing extension…' : 'Waiting for payment'}
            </h4>
            <p className="text-sm text-purple-700">
              {phase === 'requesting' ? 'Calculating and creating order' : 'Complete payment in the Razorpay window'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // === IDLE / picker UI ===
  return (
    <div className="card p-4" data-testid="session-extension">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={20} className="text-primary" />
        <h4 className="font-medium text-[#2D3748]">Extend Session</h4>
      </div>

      <p className="text-sm text-[#718096] mb-4">
        Need more time? Extend in 15-minute bundles.
      </p>

      {/* Bundle Selector */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={() => adjust(-1)}
          disabled={bundles <= MIN_BUNDLES}
          className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="extension-decrease-btn"
        >
          <Minus size={18} />
        </button>

        <div className="text-center min-w-[90px]">
          <div className="text-3xl font-bold text-primary">{minutes}</div>
          <div className="text-sm text-[#718096]">minutes</div>
        </div>

        <button
          onClick={() => adjust(1)}
          disabled={bundles >= MAX_BUNDLES}
          className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="extension-increase-btn"
        >
          <Plus size={18} />
        </button>
      </div>

      {/* Live price */}
      <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-xl mb-4">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-[#718096]" />
          <span className="text-sm text-[#718096]">Price</span>
        </div>
        <span className="font-semibold text-[#2D3748]" data-testid="extension-price-display">
          {priceLoading ? <Loader2 size={16} className="animate-spin inline-block" /> : (price?.price_display ?? '—')}
        </span>
      </div>

      <button
        onClick={startFlow}
        disabled={priceLoading || !price}
        className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="extension-pay-btn"
      >
        <CreditCard size={18} />
        Pay &amp; Extend
      </button>

      <p className="text-[11px] text-[#A0AEC0] text-center mt-3">
        Secure payment powered by Razorpay
      </p>
    </div>
  );
}
